import React from 'react';
import {
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import LabelInput from './LabelInput';
import CaptionInput from './CaptionInput';
import { useCustomTheme } from '../../../contexts/ThemeContext';

interface SelectInputProps {
    id: string;
    label: string;
    value: string;
    items: {
        id: string;
        value: string | number;
        name: string;
    }[];
    handleChange: (field: string, value: any) => void; // Updated
    handleBlur: (e: React.FocusEvent<any>) => void;
    hasSubmitted: boolean;
    errors?: string;
    touched?: boolean;
    enableLabel?: boolean;
    isMobile?: boolean;
    caption?: string;
    submitForm?: () => Promise<void>;
    sortByName?: boolean;
    pageSearch?: boolean;
}

const SelectInput: React.FC<SelectInputProps> = ({
    caption,
    id,
    label,
    value,
    items,
    hasSubmitted,
    handleBlur,
    errors,
    touched,
    handleChange,
    enableLabel,
    isMobile,
    submitForm,
    sortByName,
    pageSearch,
}) => {
    const { theme } = useCustomTheme();

    const labelId = `${id}-label`;

    const handleSelectChange = async (event: SelectChangeEvent<string>) => {
        handleChange(id, event.target.value); // Manually trigger Formik's field update
        if (submitForm) {
            await submitForm();
        }
    };

    const isValueSelected = Boolean(value); // Check if a value is selected

    return (
        <FormControl fullWidth margin="normal">
            <Box display="flex" alignItems="center">
                <Grid
                    container
                    alignItems={isMobile ? 'flex-start' : 'center'}
                    spacing={2}
                >
                    {!enableLabel ? (
                        <LabelInput
                            label={label}
                            enableLabel={enableLabel}
                            isMobile={isMobile}
                            caption={caption}
                            pageSearch={pageSearch}
                        />
                    ) : (
                        <InputLabel
                            sx={{
                                color: isValueSelected
                                    ? theme.palette.text.secondary
                                    : theme.palette.text.disabled,
                            }}
                            id={labelId}
                        >
                            {label}
                        </InputLabel>
                    )}
                    <Grid
                        item
                        xs={12}
                        sm={12}
                        md={
                            isMobile
                                ? 12
                                : enableLabel
                                  ? 12
                                  : pageSearch
                                    ? 9.2
                                    : 9.5
                        }
                        lg={enableLabel ? 12 : pageSearch ? 9 : 9.8}
                        xl={enableLabel ? 12 : pageSearch ? 9.5 : 10.25}
                    >
                        <Select
                            fullWidth
                            labelId={labelId}
                            name={id}
                            id={id}
                            value={value}
                            label={enableLabel ? label : undefined}
                            onChange={handleSelectChange} // Custom handler for Select
                            onBlur={(e) => {
                                handleBlur(e);
                            }}
                            error={touched && Boolean(errors)}
                            disabled={hasSubmitted}
                            renderValue={(selected) => {
                                const selectedItem = items.find(
                                    (item) => item.value === selected
                                );
                                return selectedItem ? (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Chip
                                            label={selectedItem.name.toUpperCase()}
                                            sx={{
                                                backgroundColor:
                                                    theme.palette.primary.main,
                                                color: theme.palette.text
                                                    .primary,
                                            }}
                                        />
                                    </Box>
                                ) : null;
                            }}
                            sx={{
                                textAlign: 'left', // Ensure the dropdown text is left-aligned
                                '& .MuiSelect-select': {
                                    textAlign: 'left', // Ensure the selected value is also left-aligned
                                },
                            }}
                        >
                            {items
                                .slice() // Create a shallow copy of the array to avoid mutating the original array
                                .sort((a, b) =>
                                    sortByName
                                        ? a.name.localeCompare(b.name)
                                        : 0
                                ) // Sort by name if sortByName is true
                                .map((item) => (
                                    <MenuItem key={item.id} value={item.value}>
                                        {item.name.toUpperCase()}
                                    </MenuItem>
                                ))}
                        </Select>
                        {touched && errors && (
                            <FormHelperText
                                sx={{ color: theme.palette.error.main }}
                            >
                                {errors}
                            </FormHelperText>
                        )}
                        {enableLabel && caption && (
                            <CaptionInput caption={caption} />
                        )}
                    </Grid>
                </Grid>
            </Box>
        </FormControl>
    );
};

export default SelectInput;
